import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import App from 'App';
import { ConfigProvider } from 'Contexts/ConfigContext';
import CustomProviders from 'Contexts/CustomProviders';
import { HeadProvider } from 'Contexts/HeadContext';
import MaintenanceProvider from 'Contexts/MaintenaceContext';
import { MessageProvider, StyledMaterialSnackbar } from 'Contexts/MessageContext';
import { FirebaseProvider } from 'FeatureModules/Auth/Contexts/FirebaseContext';
import 'lazysizes';
import mapboxgl from 'mapbox-gl';
import { SnackbarProvider } from 'notistack';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import theme from 'styles/theme';

// @ts-ignore
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default; // eslint-disable-line import/no-webpack-loader-syntax, @typescript-eslint/no-var-requires

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_DEPLOYMENT_ENV,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Tracing
  tracesSampleRate: process.env.REACT_APP_DEPLOYMENT_ENV === 'prd' ? 0.1 : 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    process.env.REACT_APP_GQL_AUTH_ENDPOINT || '',
    process.env.REACT_APP_GQL_SECURE_ENDPOINT || '',
  ],
});

/** These providers live at the top of the application and are available for child component  */
const OUTER_ROUTER_PROVIDERS = [
  { provider: ThemeProvider, props: { theme } },
  // @ts-ignore // TODO: Refactor upload provider and use tsx
  ConfigProvider as FC<PropsWithChildren>,
  FirebaseProvider,
  {
    provider: SnackbarProvider,
    props: {
      maxSnack: 5,
      preventDuplicate: true,
      Components: { error: StyledMaterialSnackbar, success: StyledMaterialSnackbar },
    },
  },
  MessageProvider,
  HeadProvider,
  MaintenanceProvider,
];

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
root.render(
  <CustomProviders providers={OUTER_ROUTER_PROVIDERS}>
    <CssBaseline />
    <App />
  </CustomProviders>
);
